import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { HelmetFunc } from 'components/PageMetaData'
import { Layout } from 'layout/Layout'
import { colors } from 'styles/colors'
import { displayWidth } from 'styles/width'

const pageMetadata = {
    uk: {
        title: 'Політика конфіденційності ClearLine',
        description:
            'Політика конфіденційності ClearLine: обробка персональних даних, отриманих через рекламні інструменти Meta (Facebook та Instagram)',
    },
    ru: {
        title: 'Политика конфиденциальности ClearLine',
        description:
            'Политика конфиденциальности ClearLine: обработка персональных данных, полученных через рекламные инструменты Meta (Facebook и Instagram)',
    },
    en: {
        title: 'ClearLine Privacy Policy',
        description:
            'ClearLine Privacy Policy: processing of personal data received through Meta advertising tools (Facebook and Instagram)',
    },
}

interface Section {
    title: string
    intro: string
    items: string[]
}

interface PrivacyContent {
    title: string
    intro: string
    sections: Section[]
    optOutTitle: string
    optOut: string
}

const content: { [lang: string]: PrivacyContent } = {
    uk: {
        title: 'Політика конфіденційності ClearLine',
        intro:
            'Ця Політика конфіденційності пояснює, як бренд ClearLine (далі — «ми») обробляє персональні дані, отримані через рекламні інструменти Meta (Facebook та Instagram).',
        sections: [
            {
                title: '1. Які дані ми збираємо',
                intro:
                    'Ми збираємо лише ту інформацію, яку ви добровільно залишаєте в Миттєвих формах:',
                items: [
                    'Номер телефону;',
                    'Адреса електронної пошти (Email).',
                ],
            },
            {
                title: '2. Мета збору даних',
                intro:
                    'Ми використовуємо ваші контакти виключно для зв’язку за вашим запитом:',
                items: [
                    'Проведення первинної консультації з архітектурного проєктування;',
                    'Надсилання опитувальника (анкети) для уточнення деталей вашого проєкту.',
                ],
            },
            {
                title: '3. Конфіденційність і безпека',
                intro: 'ClearLine гарантує, що ваші дані:',
                items: [
                    'Не будуть передані третім особам або продані;',
                    'Не будуть використані для небажаних рекламних розсилок (спаму);',
                    'Перебувають під захистом і доступні лише адміністраторам бренду для обробки вашої заявки.',
                ],
            },
        ],
        optOutTitle: '4. Відмова від обробки даних',
        optOut:
            'Ви можете будь-коли відкликати свою згоду на використання ваших контактів, написавши нам через форму зворотного зв’язку на основному сайті',
    },
    ru: {
        title: 'Политика конфиденциальности ClearLine',
        intro:
            'Настоящая Политика конфиденциальности объясняет, как бренд ClearLine (далее — «мы») обрабатывает персональные данные, полученные через рекламные инструменты Meta (Facebook и Instagram).',
        sections: [
            {
                title: '1. Какие данные мы собираем',
                intro:
                    'Мы собираем только ту информацию, которую вы добровольно оставляете в Мгновенных формах:',
                items: [
                    'Номер телефона;',
                    'Адрес электронной почты (Email).',
                ],
            },
            {
                title: '2. Цель сбора данных',
                intro:
                    'Мы используем ваши контакты исключительно для связи по вашему запросу:',
                items: [
                    'Проведение первичной консультации по архитектурному проектированию;',
                    'Отправка опросника (анкеты) для уточнения деталей вашего проекта.',
                ],
            },
            {
                title: '3. Конфиденциальность и безопасность',
                intro: 'ClearLine гарантирует, что ваши данные:',
                items: [
                    'Не будут переданы третьим лицам или проданы;',
                    'Не будут использованы для нежелательных рекламных рассылок (спама);',
                    'Находятся под защитой и доступны только администраторам бренда для обработки вашей заявки.',
                ],
            },
        ],
        optOutTitle: '4. Отказ от обработки данных',
        optOut:
            'Вы можете в любой момент отозвать свое согласие на использование ваших контактов, написав нам через форму обратной связи на основном сайте',
    },
    en: {
        title: 'ClearLine Privacy Policy',
        intro:
            'This Privacy Policy explains how the ClearLine brand (hereinafter "we") processes personal data received through Meta advertising tools (Facebook and Instagram).',
        sections: [
            {
                title: '1. What data we collect',
                intro:
                    'We collect only the information you voluntarily provide in Instant Forms:',
                items: ['Phone number;', 'Email address.'],
            },
            {
                title: '2. Purpose of data collection',
                intro:
                    'We use your contact details solely to get in touch with you regarding your request:',
                items: [
                    'Conducting an initial consultation on architectural design;',
                    'Sending a questionnaire to clarify the details of your project.',
                ],
            },
            {
                title: '3. Confidentiality and security',
                intro: 'ClearLine guarantees that your data:',
                items: [
                    'Will not be transferred to third parties or sold;',
                    'Will not be used for unsolicited advertising mailings (spam);',
                    'Is protected and accessible only to the brand administrators for processing your request.',
                ],
            },
        ],
        optOutTitle: '4. Withdrawal of consent',
        optOut:
            'You may withdraw your consent to the use of your contact details at any time by writing to us via the feedback form on the main website',
    },
}

const Wrapper = styled.div`
    max-width: 860px;
    margin: 0 auto;
    padding: 100px 20px 60px;
    color: ${colors.darkText};
    font-size: 16px;
    line-height: 26px;
    @media (min-width: ${displayWidth.tablet}) {
        padding: 140px 40px 80px;
        font-size: 18px;
        line-height: 30px;
    }
`
const Title = styled.h1`
    font-family: 'Yeseva One', sans-serif;
    font-weight: normal;
    font-size: 30px;
    line-height: 38px;
    margin-bottom: 30px;
    @media (min-width: ${displayWidth.tablet}) {
        font-size: 40px;
        line-height: 50px;
    }
`
const SubTitle = styled.h2`
    font-weight: 600;
    font-size: 20px;
    line-height: 28px;
    margin: 30px 0 12px;
`
const Text = styled.p`
    margin-bottom: 10px;
`
const List = styled.ul`
    padding-left: 24px;
    list-style: disc;
    li {
        margin-bottom: 6px;
    }
`
const Link = styled.a`
    color: ${colors.accentText};
`

const PrivacyPage = () => {
    const { i18n } = useTranslation()
    const text = content[i18n.language] || content.uk

    return (
        <Layout>
            <HelmetFunc data={pageMetadata} />
            <Wrapper>
                <Title>{text.title}</Title>
                <Text>{text.intro}</Text>

                {text.sections.map((section) => (
                    <React.Fragment key={section.title}>
                        <SubTitle>{section.title}</SubTitle>
                        <Text>{section.intro}</Text>
                        <List>
                            {section.items.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </List>
                    </React.Fragment>
                ))}

                <SubTitle>{text.optOutTitle}</SubTitle>
                <Text>
                    {text.optOut}{' '}
                    <Link href="https://clearline.com.ua/">
                        clearline.com.ua
                    </Link>
                    .
                </Text>
            </Wrapper>
        </Layout>
    )
}

export default PrivacyPage
